import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { getExportDownloadUrl } from '@/lib/api/reports-client.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useState } from 'react';
import type { useExport } from '../hooks/use-export.js';
import type { ParamValues } from './params-form.js';

type ExportState = ReturnType<typeof useExport>['state'];
type StartExport = ReturnType<typeof useExport>['startExport'];

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  reportCode: string;
  currentParams: ParamValues;
  currentViewState?: Record<string, unknown> | undefined;
  exportState: ExportState;
  startExport: StartExport;
  cancelExport: () => Promise<void>;
  resetExport: () => void;
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

function buildDefaultFilename(code: string, format: 'csv' | 'xlsx'): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `${code}_${date}.${format}`;
}

export function ExportModal({
  open,
  onClose,
  reportCode,
  currentParams,
  currentViewState,
  exportState,
  startExport,
  cancelExport,
  resetExport,
}: ExportModalProps) {
  const [format, setFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [filename, setFilename] = useState(() => buildDefaultFilename(reportCode, 'xlsx'));

  function handleFormatChange(f: 'csv' | 'xlsx') {
    setFormat(f);
    const base = filename.replace(/\.(csv|xlsx)$/, '');
    setFilename(`${base}.${f}`);
  }

  async function handleStart() {
    const key = crypto.randomUUID();
    await startExport(
      {
        params: currentParams as Record<string, unknown>,
        ...(currentViewState !== undefined ? { view_state: currentViewState } : {}),
        format,
        filename_hint: filename.replace(/\.(csv|xlsx)$/, ''),
      },
      key,
    );
  }

  async function handleDownload() {
    if (exportState.phase !== 'completed') return;
    // Use exportId directly — avoids URL-segment parsing bugs.
    // B-1: getExportDownloadUrl now returns { downloadUrl } (JSON content negotiation).
    const { downloadUrl } = await getExportDownloadUrl(exportState.exportId);
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  }

  function handleClose() {
    resetExport();
    onClose();
  }

  const isIdle = exportState.phase === 'idle';
  const isStarting = exportState.phase === 'starting';
  const isPolling = exportState.phase === 'polling';
  const isCompleted = exportState.phase === 'completed';
  const isFailed = exportState.phase === 'failed';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('export.title')}</DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Format selector */}
          <div>
            {/* biome-ignore lint/a11y/noLabelWithoutControl: label describes a group of buttons, not a single control */}
            <label className="mb-1.5 block text-xs font-medium text-[var(--color-qup-text-muted-dark)]">
              {t('export.format')}
            </label>
            <div className="flex gap-2">
              {(['csv', 'xlsx'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    if (isIdle) handleFormatChange(f);
                  }}
                  disabled={!isIdle}
                  className={cn(
                    'flex-1 rounded-[var(--radius-button)] border px-3 py-2 text-sm font-medium transition-all duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    format === f
                      ? 'border-[var(--color-qup-primary)] bg-[var(--color-qup-primary)]/15 text-[var(--color-qup-primary)]'
                      : 'border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-dark)] hover:bg-[var(--color-qup-bg-dark)]',
                  )}
                >
                  {f === 'csv' ? t('export.format_csv') : t('export.format_xlsx')}
                </button>
              ))}
              <div
                className={cn(
                  'flex-1 rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)] px-3 py-2 text-center text-sm',
                  'text-[var(--color-qup-text-muted-dark)] opacity-50 cursor-not-allowed',
                )}
              >
                {t('export.format_pdf')}
              </div>
            </div>
          </div>

          {/* Filename */}
          <div>
            <label
              htmlFor="export-filename"
              className="mb-1.5 block text-xs font-medium text-[var(--color-qup-text-muted-dark)]"
            >
              {t('export.filename')}
            </label>
            <Input
              id="export-filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              disabled={!isIdle}
            />
          </div>

          {/* Progress */}
          {(isStarting || isPolling) && (
            <div>
              <div className="mb-1 flex items-center justify-between text-xs text-[var(--color-qup-text-muted-dark)]">
                <span>{t('export.preparing')}</span>
                {isPolling && (
                  <span>{interpolate(t('export.progress'), { n: exportState.progress ?? 0 })}</span>
                )}
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-qup-border-dark)]">
                {/* biome-ignore lint/a11y/useFocusableInteractive: progress bars are informational; screen readers access via aria-valuenow */}
                <div
                  className="h-full rounded-full bg-[var(--color-qup-primary)] transition-all duration-300"
                  style={{ width: `${isPolling ? (exportState.progress ?? 10) : 10}%` }}
                  role="progressbar"
                  aria-valuenow={isPolling ? (exportState.progress ?? 0) : 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          )}

          {/* Completed */}
          {isCompleted && (
            <div className="flex items-center gap-2 rounded-[var(--radius-card)] bg-[var(--color-qup-success)]/10 px-3 py-2 text-sm text-[var(--color-qup-success)]">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
              {t('export.completed')}
            </div>
          )}

          {/* Failed */}
          {isFailed && (
            <div className="flex items-center gap-2 rounded-[var(--radius-card)] bg-[var(--color-qup-error)]/10 px-3 py-2 text-sm text-[var(--color-qup-error)]">
              {t('export.failed')}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-2">
          {(isPolling || isStarting) && (
            <Button type="button" variant="outline" size="sm" onClick={cancelExport}>
              {t('export.cancel')}
            </Button>
          )}

          {isCompleted && (
            <Button type="button" variant="primary" size="sm" onClick={handleDownload}>
              {t('export.download')}
            </Button>
          )}

          {isIdle && (
            <>
              <DialogClose asChild>
                <Button type="button" variant="outline" size="sm">
                  {t('export.cancel')}
                </Button>
              </DialogClose>
              <Button type="button" variant="primary" size="sm" onClick={handleStart}>
                {t('export.start')}
              </Button>
            </>
          )}

          {(isFailed || isCompleted || exportState.phase === 'cancelled') && (
            <Button type="button" variant="outline" size="sm" onClick={handleClose}>
              Cerrar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
